import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxOpen, FaCalendarAlt, FaChartLine, FaCog, FaEdit, FaEnvelope, FaEye, FaFileDownload, FaFileUpload, FaList, FaMapMarkerAlt, FaMoneyBill, FaMoneyBillWave, FaPlus, FaPrint, FaQrcode, FaReceipt, FaSearch, FaStore, FaTag, FaTh, FaTicketAlt, FaTrash, FaUser, FaWhatsapp } from 'react-icons/fa';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { saleActions } from '../../sagas/saleSlice';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import autoTable from 'jspdf-autotable';
import { formatPrice } from '../../helpers/utility';
const SalesReport = () => {
    const [criteria, setSearchCriteria] = useState({ query: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);
    const [isSearching, setIsSearching] = useState(false);
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();
    const {
        items,
        sales,
        isLoading,
        error
    } = useSelector((state) => state.sales);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [showItemsModal, setShowItemsModal] = useState(false);
    const { businessName, email, address, telephone, fullName } = useSelector(state => state.auth);
    const navigate = useNavigate();
    const [filterType, setFilterType] = useState('today');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [displayMode, setDisplayMode] = useState('details'); // or 'tickets'


    useEffect(() => {
        let payload = {
            filterType,
            startDate: filterType === 'custom' ? startDate : null,
            endDate: filterType === 'custom' ? endDate : null,
            query: searchTerm
        };

        if (displayMode === 'tickets') {
            dispatch(saleActions.search(payload));
        } else {
            dispatch(saleActions.filterSales(payload));
        }

    }, [filterType, startDate, endDate, displayMode]);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [items, sales]);

    const totalSalesAmount = items ? items.reduce((total, item) => total + item.total, 0) : 0;

    // Debounced search function
    const debouncedSearch =
        debounce((query) => {
            if (displayMode === 'tickets') {
                dispatch(saleActions.search({
                    filterType,
                    startDate: filterType === 'custom' ? startDate : null,
                    endDate: filterType === 'custom' ? endDate : null,
                    query,
                    onSuccess: () => {
                        //setSearchTerm('');
                        //setIsSearching(false);
                        searchInputRef.current?.focus();
                    }
                }));
            } else {
                dispatch(saleActions.filterSales({
                    filterType,
                    startDate: filterType === 'custom' ? startDate : null,
                    endDate: filterType === 'custom' ? endDate : null,
                    query,
                    onSuccess: () => {
                        //setSearchTerm('');
                        //setIsSearching(false);
                        searchInputRef.current?.focus();
                    }
                }));
            }

        }, 1500);


    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.length > 5 || value.length === 0) { 
            debouncedSearch(value);
        }
        
    };


    const handlePrint = async () => {
        try {
            const doc = new jsPDF('p', 'mm', 'a4'); // 'p' for portrait, 'mm' for millimeters, 'a4' for paper size


            const headers = [
                [
                    t('sale.receipt.date'),
                    t('product.code'),
                    t('product.description'),
                    t('sale.receipt.quantity'),
                    t('product.salePrice'),
                    t('sale.receipt.discount'),
                    t('sale.receipt.total'),
                ],
            ];

            // Add table rows
            const rows = items.map((item) => [
                item.saleDate,
                item.productBarcode,
                item.productDescription,
                item.quantity,
                formatPrice(item.price, false),
                formatPrice(item.discount, false),
                formatPrice(item.total, false),
            ]);

            // Add table to PDF
            autoTable(doc, {
                startY: 60,
                head: headers,
                body: rows,
                theme: 'grid',
                styles: { fontSize: 10 },
                headStyles: { fillColor: [41, 128, 185] }, // Blue header
            });

            // Add total sales amount
            const finalY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(12);
            doc.text(`${t('sale.totalSalesAmount')}: ${formatPrice(totalSalesAmount)}`, 10, finalY);

            // Add footer
            doc.setFontSize(10);
            doc.text(t('sale.receipt.thankYou'), 105, 290, { align: 'center' });
            doc.text(t('sale.receipt.footer'), 105, 295, { align: 'center' });

            // Save or open the PDF
            doc.save(`Sales_Report.pdf`);
        } catch (error) {
            console.error('PDF generation error:', error);
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: t('sale.receipt.printError'),
            });
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            debouncedSearch(searchTerm); // Trigger search when Enter is pressed
        }
    };

    const generatePDF = async (id) => {
        try {
            let payload = {
                id: id,
                onSuccess: (response) => {
                    const pdfWindow = window.open();
                    pdfWindow.document.write(
                        "<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
                        encodeURI(response) +
                        "'></iframe>"
                    );
                },
            }

            dispatch(saleActions.printReceipt(payload))

        } catch (error) {
            console.error('PDF generation error:', error);
        }
    };


    const DisplayToggle = () => (
        <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="btn-group">
                <Button
                    color={displayMode === 'details' ? 'primary' : 'light'}
                    onClick={() => setDisplayMode('details')}
                    size="sm"
                >
                    <FaList className="me-1" /> {t('common.details')}
                </Button>
                <Button
                    color={displayMode === 'tickets' ? 'primary' : 'light'}
                    onClick={() => setDisplayMode('tickets')}
                    size="sm"
                >
                    <FaTh className="me-1" /> {t('common.tickets')}
                </Button>
            </div>
        </div>
    );


    const TicketsView = () => (
        <>
            <Table bordered hover>
                <thead>
                    <tr>
                        <th>{t('sale.receipt.receiptNo')}</th>
                        <th>{t('sale.receipt.date')}</th>
                        <th>{t('sale.receipt.cashier')}</th>
                        <th>{t('sale.receipt.total')}</th>
                        <th className="text-center">{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {!sales || sales.length === 0 ? (
                        <tr>
                            <td colSpan="6" className="text-center py-4">
                                <div className="text-muted">
                                    {t('product.noProducts')}
                                </div>
                            </td>
                        </tr>
                    ) : (sales.map((sale) => (
                        <tr key={sale.numero}>
                            <td>{sale.numero}</td>
                            <td>{sale.saleDate}</td>
                            <td>{sale.createdBy}</td>
                            <td className="text-end">{formatPrice(sale.totalAmount)}</td>
                            <td className="text-center">
                                <Button color="primary" onClick={() => generatePDF(sale.id)}>
                                    <FaPrint className="me-1" />
                                    {t('receipt.printBill')}
                                </Button>
                                <Button color="warning" className="ms-2" onClick={() => navigate(`/sales/edit/${sale.id}`)}>
                                    <FaEdit className="me-1" />
                                    {t('common.edit')}
                                </Button>
                            </td>
                        </tr>
                    ))
                    )}
                </tbody>
            </Table>
        </>
    );

    const DetailsView = () => (
        <>
            <Table hover bordered className="align-middle mb-0">
                <thead className="bg-light">
                    <tr>
                        <th className="align-middle">
                            <FaReceipt className="me-2 text-primary" size={12} />
                            {t('sale.receipt.date')}
                        </th>
                        <th className="align-middle">
                            <FaReceipt className="me-2 text-primary" size={12} />
                            {t('sale.receipt.caissier')}
                        </th>
                        <th className="align-middle">
                            <FaReceipt className="me-2 text-primary" size={12} />
                            {t('product.code')}
                        </th>
                        <th className="align-middle">
                            <FaBox className="me-2 text-primary" size={12} />
                            {t('product.description')}
                        </th>
                        <th className="align-middle">
                            <FaBox className="me-2 text-primary" size={12} />
                            {t('sale.receipt.quantity')}
                        </th>
                        <th className="align-middle text-center">
                            <FaTag className="me-2 text-primary" size={12} />
                            {t('product.salePrice')}
                        </th>
                        <th className="align-middle text-center">
                            <FaTag className="me-2 text-primary" size={12} />
                            {t('sale.receipt.discount')}
                        </th>
                        <th className="align-middle text-center">
                            <FaTag className="me-2 text-primary" size={12} />
                            {t('sale.receipt.total')}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {!items || items.length === 0 ? (
                        <tr>
                            <td colSpan="6" className="text-center py-4">
                                <div className="text-muted">
                                    {t('sale.noSales')}
                                </div>
                            </td>
                        </tr>
                    ) : (
                        items.map((item) => (
                            <tr key={item.id}>
                                <td className="align-middle">{item.saleDate}</td>
                                <td className="align-middle">{item.createdBy}</td>
                                <td className="align-middle">{item.productBarcode}</td>
                                <td className="align-middle">{item.productDescription}</td>
                                <td className="align-middle">{item.quantity}</td>
                                <td className="align-middle text-end">{formatPrice(item.price)}</td>
                                <td className="align-middle text-end">{formatPrice(item.discount)}</td>
                                <td className="align-middle text-end">{formatPrice(item.total)}</td>

                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
        </>
    );



    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaChartLine className="text-primary me-2" />
                                    {t('sale.pageTitle')}
                                </h4>
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        color="primary"
                                        className="d-flex align-items-center"
                                        onClick={handlePrint} // Trigger the handlePrint function
                                        title={t('sale.print')}
                                    >
                                        <FaPrint className="me-2" />
                                        {t('sale.print')}
                                    </Button>
                                    <Button
                                        color="primary"
                                        className="d-flex align-items-center"
                                        onClick={() => navigate('/sales')}
                                        title={t('sale.newSale')}
                                    >
                                        <FaPlus className="me-2" />
                                        {t('sale.newSale')}
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col md={12} className="pe-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                    <div className="mt-2 text-muted">
                                        {t('common.loadingData')}
                                    </div>
                                </div>
                            ) : (<>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    {/* Search Input - Left Side */}
                                    <div className="position-relative flex-grow-1 me-3">
                                        <Input
                                            innerRef={searchInputRef}
                                            type="text"
                                            placeholder={t('product.searchPlaceholder')}
                                            value={searchTerm}
                                            onKeyDown={handleSearchKeyDown}
                                            onChange={handleSearchChange}
                                            className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                        />
                                        <FaSearch
                                            style={{
                                                position: 'absolute',
                                                left: '10px',
                                                top: '12px',
                                                color: '#6c757d'
                                            }}
                                        />
                                    </div>
                                    {/* Filter Section - Right Side */}
                                    <div className="d-flex align-items-center">
                                        <FormGroup className="me-3">
                                            <Label for="filterType">{t('sale.filter')}</Label>
                                            <Input
                                                type="select"
                                                id="filterType"
                                                value={filterType}
                                                onChange={(e) => setFilterType(e.target.value)}
                                            >
                                                <option value="today">{t('sale.filterToday')}</option>
                                                <option value="week">{t('sale.filterWeek')}</option>
                                                <option value="month">{t('sale.filterMonth')}</option>
                                                <option value="year">{t('sale.filterYear')}</option>
                                                <option value="custom">{t('sale.filterCustom')}</option>
                                            </Input>
                                        </FormGroup>
                                        {filterType === 'custom' && (
                                            <>
                                                <FormGroup className="me-3">
                                                    <Label for="startDate">{t('sale.startDate')}</Label>
                                                    <Input
                                                        type="date"
                                                        id="startDate"
                                                        value={startDate.toISOString().split('T')[0]}
                                                        onChange={(e) => setStartDate(new Date(e.target.value))}
                                                        className="form-control"
                                                    />
                                                </FormGroup>
                                                <FormGroup className="me-3">
                                                    <Label for="endDate">{t('sale.endDate')}</Label>
                                                    <Input
                                                        type="date"
                                                        id="endDate"
                                                        value={endDate.toISOString().split('T')[0]}
                                                        onChange={(e) => setEndDate(new Date(e.target.value))}
                                                        className="form-control"
                                                    />
                                                </FormGroup>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <DisplayToggle />
                                <div className="table-responsive" style={{ overflowY: 'auto', maxHeight: '450px' }}>
                                    {displayMode === 'tickets' ? <TicketsView /> : <DetailsView />}
                                </div>
                                <div className="d-flex justify-content-end mt-3">
                                    <h5>{t('sale.totalSalesAmount')}: {formatPrice(totalSalesAmount)}</h5>
                                </div>

                            </>)}
                        </CardBody>
                    </Card>
                </Col>
            </Row>


        </Container>

    );
};

export default SalesReport;
